// SubscriptionPlanProfileService.tsx
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { SubscriptionPlanProfileService } from '../../core/services';
import { PageSize } from '../../type';
import { GetSubscriptionPlanByBusiness } from './interface';

export const useGetSubscriptionPlanService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-subscription-plans-data'],
      queryFn: async ({ pageParam }) => {
        return SubscriptionPlanProfileService.getSubscriptionPlans({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          title: initialParams.title,
          billingCycle: initialParams.billingCycle,
        });
      },
      enabled: false,
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetSubscriptionPlanDropdownService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-subscription-plans-dropdown-data'],
      queryFn: async ({ pageParam }) => {
        return SubscriptionPlanProfileService.getSubscriptionPlansDropdown({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          title: initialParams.title,
        });
      },
      enabled: false,
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetFeaturesListService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-features-list-data'],
      queryFn: async ({ pageParam }) => {
        return SubscriptionPlanProfileService.getFeaturesList({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
        });
      },
      enabled: false,
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetSubscriptionPlanByBusinessService = (id: string | number | undefined) => {
  const { data, isLoading, isRefetching, error, refetch } = useQuery<GetSubscriptionPlanByBusiness, unknown>({
    queryKey: ['get-subscription-plan-by-business-data'],
    queryFn: async () => {
      return SubscriptionPlanProfileService.getSubscriptionPlanByBusiness(id);
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    isRefetching,
    error,
    refetch,
  };
};

export const useGetSubscriptionPlanHistoryService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-subscription-plan-history-data'],
      queryFn: async ({ pageParam }) => {
        return SubscriptionPlanProfileService.getSubscriptionPlansHistory({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          businessPlanId: initialParams.businessPlanId,
        });
      },
      enabled: false,
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};
